import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

const CATS = ["Furniture", "Electronics", "Clothing & Fashion", "Kitchen", "Baby & Kids", "Books", "Cultural Goods", "Beauty & Wellness", "Vehicles", "Free / Giveaway", "Other"];
const CONDS = ["New", "Like New", "Used", "For Parts"];

export default function CreateListing() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Cultural Goods");
  const [condition, setCondition] = useState("Used");
  const [price, setPrice] = useState("");
  const [isFree, setIsFree] = useState(false);
  const [location, setLocation] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!authLoading && !user) nav("/login"); }, [authLoading, user, nav]);

  useEffect(() => {
    if (!editing) return;
    api.get(`/listings/${id}`).then(({ data }) => {
      setTitle(data.title || "");
      setDescription(data.description || "");
      setCategory(data.category || "Cultural Goods");
      setCondition(data.condition || "Used");
      setPrice(data.price ? String(data.price) : "");
      setIsFree(Boolean(data.is_free));
      setLocation(data.location || "");
      setContactMethod(data.contact_method || "");
      setImages(data.images || []);
    });
  }, [id, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return toast.error("Title and description are required");
    setSaving(true);
    const payload = {
      title, description, category, condition,
      price: isFree ? 0 : Number(price) || 0,
      is_free: isFree, location, contact_method: contactMethod, images,
    };
    try {
      const { data } = editing
        ? await api.patch(`/listings/${id}`, payload)
        : await api.post("/listings", payload);
      toast.success(editing ? "Listing updated" : "Listing published");
      nav(`/marketplace/${data.id}`);
    } catch (e) {
      toast.error("Failed to save listing");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">{editing ? "Edit" : "New listing"}</div>
      <h1 className="font-display text-4xl text-[#1E3A2F] mt-1">{editing ? "Edit your listing" : "Post something to the community"}</h1>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-[#E2DED0] rounded-2xl p-6 sm:p-8">
        <div>
          <Label>Photos</Label>
          <div className="mt-2"><ImageUploader value={images} onChange={setImages} max={6} /></div>
        </div>

        <div>
          <Label>Title</Label>
          <Input data-testid="listing-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ethiopian coffee ceremony set" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="listing-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Condition</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="listing-condition"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <div>
            <Label>Price (USD)</Label>
            <Input data-testid="listing-price" type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} disabled={isFree} placeholder="0" />
          </div>
          <label className="inline-flex items-center gap-2 pb-2.5">
            <Checkbox data-testid="listing-free" checked={isFree} onCheckedChange={(v) => setIsFree(Boolean(v))} />
            <span>Give it away for free</span>
          </label>
        </div>

        <div>
          <Label>Location</Label>
          <Input data-testid="listing-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Renton, WA" />
        </div>

        <div>
          <Label>Description</Label>
          <Textarea data-testid="listing-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={6} placeholder="Tell buyers about it — story, condition, pickup details." />
        </div>

        <div>
          <Label>How should people reach you?</Label>
          <Textarea data-testid="listing-contact" value={contactMethod} onChange={(e) => setContactMethod(e.target.value)} rows={2} placeholder="Text 206-555-0142 or DM on WhatsApp" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => nav(-1)}>Cancel</Button>
          <Button data-testid="listing-submit" type="submit" disabled={saving} className="abm-btn-primary rounded-full px-6">
            {saving ? "Saving…" : editing ? "Save changes" : "Publish listing"}
          </Button>
        </div>
      </form>
    </div>
  );
}
